import {O} from "web-vcore";
import {Vec2} from "../../../../Utils/ThreeJS/VectorStructs.js";
import type {ComponentBackend, ComponentBackendSelection, SmootherVariant} from "../../../../Terrain/Heights/Poisson/Solve/Solve_Common.js";
import {ignore} from "mobx-sync";

// 0 = global/base
// 1 = TBD
export type FocusLevel = 0 | 1;
export type FocusLevel_NonBase = 1;

export enum AnchorDisplayLayer {
	low = "low",
	mid = "mid",
	high = "high",
}

export enum MapSettingsTab {
	general = "general",
	terrain = "terrain",
	gpu = "gpu",
	view3D = "view3D",
}

/** Set14 P7 §4.11 — the tone-mapping MODE (an enum, NOT a boolean). Maps 1:1 onto three's
 *  `ToneMapping` constants for the terrain + scenery output transfer chain. Value 0
 *  (`NoToneMapping`) = the pre-P7 terrain look (no tone-map, and with a LINEAR output colour
 *  space the sRGB encode is the identity → byte-identical to before); 4 (`ACESFilmic`) = the
 *  R3F default = the recommended setting the terrain was tuned for. `CustomToneMapping` (5) is
 *  DELIBERATELY excluded — it requires a caller-supplied `toneMappingFn` GLSL and exposing it
 *  without one yields a broken shader. */
export enum ToneMappingMode {
	NoToneMapping = 0,
	LinearToneMapping = 1,
	ReinhardToneMapping = 2,
	CineonToneMapping = 3,
	ACESFilmicToneMapping = 4,
	AgXToneMapping = 6,
	NeutralToneMapping = 7,
}
export const ToneMappingMode_options = [
	{name: "None", value: ToneMappingMode.NoToneMapping},
	{name: "Linear", value: ToneMappingMode.LinearToneMapping},
	{name: "Reinhard", value: ToneMappingMode.ReinhardToneMapping},
	{name: "Cineon", value: ToneMappingMode.CineonToneMapping},
	{name: "ACES filmic (default)", value: ToneMappingMode.ACESFilmicToneMapping},
	{name: "AgX", value: ToneMappingMode.AgXToneMapping},
	{name: "Neutral", value: ToneMappingMode.NeutralToneMapping},
];

export class MapSettings {
	@O accessor tab = MapSettingsTab.general;
	@O accessor general = new MapSettings_General();
	@O accessor terrain = new MapSettings_Terrain();
	@O accessor gpu = new MapSettings_GPU();
	/** Set15 P1 — the 3D-mode view settings. */
	@O accessor view3D = new MapSettings_View3D();
}

export class MapSettings_General {
	@O accessor screenshotMode = false;
	@O accessor focusL1_enabled = false;
	@O accessor focusL1_size = new Vec2(100, 100); // 100x100 meters
	@O accessor focusL1_show = true;
	@O accessor focusL1_lockPos: Vec2|n;
	@O accessor showLabels = true;
	@O accessor showShapes = true;
	@O accessor showGuideGrids = false;
	@O accessor showViewpoints = false;
	/** Set? — caps each viewpoint wedge's reach to this many metres from the eye (useful for
	 *  pitch-0 cameras whose ground reach otherwise extends to near-infinity). Unset/≤0 = no clamp. */
	@O accessor viewpoints_maxProjectionDistance: number|n;
	@O @ignore accessor showAnchors = false;
	@O accessor anchors_historyDepth = 1;
	@O accessor anchors_layer = AnchorDisplayLayer.mid;
	/** Percentage of the screen width. (ex: .1 = 10%) */
	@O accessor anchors_size = .1;
	@O accessor showReferences = false;
	@O accessor references_opacity: number|n;
	@O accessor references = [] as SizeReferenceEntry[];
	@O accessor forceContinuousRendering = false;
	/** Set15 P5 debug — `true` keeps the WebGL drawing buffer after each frame (via the Canvas
	 *  `gl.preserveDrawingBuffer`), so the agent/devtools can read actual rendered pixels with
	 *  `readPixels` instead of getting all-zeroes. Default off (matches production perf). */
	@O accessor debug_preserveDrawingBuffer = false;
}

export class MapSettings_Terrain {
	/** When false, NO scenery-instance processing happens for rendering (the `SceneryRenderer` is
	 *  unmounted entirely — placement/bake/instance generation for the map view are all skipped).
	 *  Orthogonal to the scenery systems that stay enabled regardless (kind-editor previews, the
	 *  path-driven scenery regions the terrain/materials bridge reads). */
	@O accessor showScenery = true;
	// Terrain elevation overlay
	@O accessor overlay_enabled = true;
	/** When false, heights skip the Poisson solver and every chunk renders at the flat fallback height (near-instant material fills in 2D). */
	@O accessor overlay_heightSolverEnabled = true;
	/** The global terrain height (metres) used when the height solver is off. Default 0. */
	@O accessor overlay_heightFallback = 0;
	@O accessor overlay_workerCount = 2;
	/** Chunks per `evalChunkBatch` message. Amortises per-message overhead (decode + postMessage
	 * + worker's camChanged/coalesce dance) across N chunks. Observed eval-chunk region durations:
	 *  (1: 134ms, 4: 98ms, 16: 91ms, 32: 85ms, 64: 92ms, 128: 100–340ms) [rec: 16 or 32] */
	@O accessor overlay_evalChunkBatchSize = 32;
	@O accessor overlay_opacity = 0.5;

	/** **Render-chunk LOD threshold** (px per sample): a chunk splits when its on-screen size exceeds
	 *  `CHUNK_N × renderChunk_pxPerSample` pixels (= 33 × this).  Lower = sharper (more/finer chunks),
	 *  higher = blurrier (fewer chunks).  Default 4 ≈ 4px per sample.  Conceptually separate from
	 *  {@link mode2D_solverFineCell_sizeAsPx} (the solver's requested fine-cell size). */
	@O accessor renderChunk_maxPxPerSample = 4;
	/** **Set15.5 P2 — the render-lattice FLOOR** (samples per solved fine-cell edge): the render chunk's
	 *  sample interval can never be FINER than `solvedCellSize ÷ this` metres, which (per CHUNK_N − 1 =
	 *  32 intervals across 33 samples) locks the terrain-chunk width to **≥ ~32 × that** — i.e. `32 ×
	 *  solvedCellSize ÷ this` metres.  Example (the §4.1 docblock contract): if the solved fine-cell is
	 *  2×2 m and this is 2, the sample interval cannot be smaller than 1 m → the terrain-chunk size is
	 *  at least ~32 m.
	 *
	 *  This is the tree's **ONLY depth guard** (`updateNode`/`split` have no depth cap), so it MUST NOT
	 *  be disable-able: clamped to `[0.25, 64]` (validated default — R1), where a lower value = a COARSER
	 *  allowed lattice (a larger floor) = cheaper + bounded depth, and a higher value = a finer allowed
	 *  lattice.  Sibling to {@link renderChunk_maxPxPerSample} (the px-per-sample LOD threshold); this
	 *  knob **overrides** it when it binds (the floor wins — you cannot sample finer than the solved
	 *  field).  The floor is applied to the PERSPECTIVE (3D) branch only; in 2D the lattice is already
	 *  screen-coupled to the solve (so the ortho path stays byte-identical, §9).  The floor base is the
	 *  REALIZED solved cell size (not the request), bounded by the finest L octave (§4.5 forward-compat). */
	@O accessor renderChunk_maxSamplesPerFineCellEdge = 2;
	/** **Solver fine-cell size — 2D MODE ONLY** (as px per sample): the renderer projects this to the
	 *  working-grid / ring-0 solve request via `screenFineCellSize = mode2D_solverFineCell_sizeAsPx / worldToScreen`.
	 *  Lower = finer solver cells (sharper solved terrain), higher = coarser (cheaper).  Default 4 ≈ 4px
	 *  per sample.  In 3D mode this knob is IGNORED — the 3D solve cell is pinned to the terrain-definition's
	 *  `mode3D_fineCellSize` (independent of the screen projection).  Conceptually separate from
	 *  {@link renderChunk_maxPxPerSample} (the LOD chunk-split threshold) — the two were formerly one knob
	 *  (`overlay_maxPxPerSample`); splitting them lets the solve resolution be tuned independently of the
	 *  render-chunk density. */
	@O accessor mode2D_solverFineCell_sizeAsPx = 4;

	/** V3.1: debug overlay (plan §2.3) painting solved AMR cells by type, refinement level,
	 *  and per-solve L² residual. Off by default — adds a snapshot-build cost per
	 *  `prepareTerrain` dispatch (camera-window-bounded per §7.1). */
	@O accessor overlay_showConstraintCells = false;
	/** Set9 Phase 3: procedural-materials render path (fragment-shader terrain surfacing in
	 *  world space) vs the legacy CPU-baked 33×33 albedo bitmap.  Off by default — the highest-risk
	 *  phase stays one toggle away from the old path during bring-up (Set9 plan §9 Phase 3 ⚠).
	 *  When on, the renderer drops the ImageBitmap bake and draws each chunk with the assembled
	 *  material shader; Phase 5 added the splat field (≤4 materials per chunk from the demo
	 *  elevation-band assignments; the remaining recipes arrive in Phase 8). */
	@O accessor proceduralMaterials = false;
	/** 2026-08-25 — DEBUG SHIM for the procedural-materials render path.  When ON (only
	 *  meaningful alongside `proceduralMaterials`), the terrain is NOT drawn with its real
	 *  assembled material shaders — instead every chunk renders with a single trivial "debug
	 *  shim" fragment shader that just paints the SPLAT-ASSIGNED materials as solid-ish,
	 *  hashed colours (plus a per-material strip angle to disambiguate neighbours).
	 *
	 *  WHY: the Set16/Set17 procedural-materials compilers emit very large, complex fragment
	 *  shaders, and in vscode's integrated browser (SwiftShader/ANGLE) the compile+link of even
	 *  ONE such program is the dominant per-pan jank — measured tens of ms per compile × dozens
	 *  of chunk sets = the "gl context lost"/freeze wall.  This shim trades the real look for a
	 *  single always-cached, tiny shader so the user can still *visually distinguish which
	 *  materials cover which regions* (for debugging placement/coverage) without the massive
	 *  compile cost.
	 *
	 *  Architecturally a *cache bypass*, not a new path: while ON, `ensureProgram`/`warmUpPrograms`
	 *  compile ONLY the one debug-shim program (one structuralHash → one WebGLProgram) and every
	 *  chunk clones from that single template (still binding its real splat textures + variant
	 *  row, so the per-chunk geometry/uniform plumbing is exercised byte-identically).  Off = the
	 *  full real-material compile path (unchanged). */
	@O accessor debugShimMats = false;

	// Set14 P7 §4.11 — the OUTPUT TRANSFER CHAIN setting. This drives the renderer's
	// `toneMapping` (and, under `NoToneMapping`, a LINEAR output colour space so the sRGB encode
	// is the identity) — the SAME setting the scenery bake captures into its signature, so a
	// change here re-bakes the tree atlases. Default ACES = R3F's own default = the look the
	// terrain system was tuned for. See the ToneMappingMode enum for the mode list + why
	// CustomToneMapping (5) is excluded.
	/** The tone-mapping mode applied to the terrain + scenery output chain. Default ACESFilmic (4). */
	@O accessor toneMapping = ToneMappingMode.ACESFilmicToneMapping;
}

/** Set6: per component/phase backend selection for the multigrid V-cycle phases.
 *  Each field routes one phase to `"cpu" | "webGL" | "webGPU"`; falls back to CPU if unavailable. */
export class MapSettings_GPU implements ComponentBackendSelection {
	/** Fine-level (5-point Laplace) Gauss-Seidel smoother backend.
	 *  `webGPU` = WGSL compute (fastest, Chromium ≥ 113); `webGL` = GLSL-ES-300 fragment
	 *  (fallback for unstable-WebGPU hosts); `cpu` = reference path (byte-faithful; slowest). */
	@O accessor fineSmoother: ComponentBackend = "cpu";
	/** Backend for the FIRST-coarsening Galerkin CSR build (`buildGalerkinStencil`, the
	 *  dominant per-pan cost).  CPU default — the GPU/WebGL ports ship but stay opt-in
	 *  (the GPU shader only handles the first coarsening; inner coarsenings are CPU). */
	@O accessor galerkinBuild: ComponentBackend = "cpu";
	/** Backend for the Galerkin 9-point CSR coarse-level Gauss-Seidel smoother.  `webGPU`
	 *  ships BOTH the lex + wavefront variants (see `preferredSmootherVariant`); `webGL`
	 *  ships wavefront only. */
	@O accessor galSmoother: ComponentBackend = "cpu";
	/** Backend for coarse-grid topology + GPU/WebGL buffer-pool allocation/upload.
	 *  AMR topology (`Hierarchy_CPU.ts`) is intrinsically CPU; this controls only the
	 *  buffer pool (`Hierarchy_WebGPU/WebGL.ts`), falling back to CPU if unregistered. */
	@O accessor hierarchy: ComponentBackend = "cpu";
	/** Backend for the prolongation operator (coarse→fine correction transfer). */
	@O accessor prolong: ComponentBackend = "cpu";
	/** Backend for the residual operator (fine→coarse defect computation). */
	@O accessor residual: ComponentBackend = "cpu";
	/** Backend for the restriction operator (fine→coarse residual transfer). */
	@O accessor restrict: ComponentBackend = "cpu";
	/** Backend for the coarsest-level direct LU solve (`DenseLinear.ts`).  CPU default —
	 *  the coarsest direct solve is tiny + intrinsically CPU; the selector is kept for
	 *  symmetry (a future LU-on-GPU port could honour it). */
	@O accessor solve: ComponentBackend = "cpu";
	/** Preferred coarse smoother variant (lex vs wavefront).  Honoured on backends that
	 *  ship both (WebGPU); silently picks the only-available variant on backends that
	 *  ship one (WebGL ships wavefront only).  See {@link SmootherVariant}. */
	@O accessor preferredSmootherVariant: SmootherVariant = "wavefront";
}

/** Set15 P1 — the 3D-mode (locked-viewpoint) view settings.  Per plan §5, activation state is UI
 *  state (persisted here via mobx-sync) and NOT DB: `view3D_enabled` toggles the whole render
 *  mode; `view3D_activeViewpointObjID` selects WHICH `Mod_Viewpoint` object the camera comes from.
 *
 *  Per §8 R1, every numeric knob is resolved through a validated default before any comparison
 *  (a persisted-but-undefined field must not silently NaN out a guard). */
export class MapSettings_View3D {
	/** Set15 P1 — is 3D mode enabled? When true, `MapSurface` remounts its `<Canvas>` with a
	 *  `PerspectiveCamera` derived from the active `Mod_Viewpoint`, renders only the §5
	 *  allow-list (TerrainRenderer + SceneryRenderer + sky/fog), and hard-gates tools/controls/
	 *  overlays.  Default off — 2D stays the primary authoring surface. */
	@O accessor view3D_enabled = false;
	/** The `MapObject.id` carrying the active `Mod_Viewpoint`. Null = no viewpoint selected (3D
	 *  shows the "place a viewpoint" hint / falls back). */
	@O accessor view3D_activeViewpointObjID: string|n;

	/** Far view distance (metres) — the perspective camera's far plane, the quadtree LRU cap's
	 *  world-size basis, and the fog curve's target, kept consistent.  Default = full world extent
	 *  (D14: terrain to the horizon). */
	@O accessor maxViewDistance: number|n;
	/** Exponential fog density (1/metres). Tuned to `maxViewDistance` by default (null = derive). */
	@O accessor fogDensity: number|n;
	/** Set15 P4 — the 3D scenery draw distance (metres): beyond this distance from the eye, no
	 *  scenery instances are placed (D14: terrain reaches the 50 km horizon, scenery cuts off
	 *  closer).  Default 5 km.  Also drives the far-field distance-based rank thinning (mpp at
	 *  the tile's distance — §3.8).  Validated via `resolveSceneryMaxDistance`. */
	@O accessor view3D_sceneryMaxDistance: number|n;

	/** Set15 P8 (§6.2) — are 3D shadows enabled? A one-time render (static scene + static light +
	 *  locked camera) makes them cheap. Default true. */
	@O accessor view3D_shadowsEnabled: boolean|n;
	/** Set15 P8 (§6.2) — the near-field shadow-map resolution (px per side; floored to a power of
	 *  two).  The locked camera pins where the legible near field is, so ONE tight map over it
	 *  suffices (cascades are a future generalisation).  Default 2048.  Validated via
	 *  `resolveShadowMapSize`. */
	@O accessor view3D_shadowMapSize: number|n;
	/** Set15 P8 (§6.2) — the ortho shadow-camera half-extent (metres) around the eye: the near
	 *  field the single shadow map covers.  Default 600 m.  Validated via `resolveShadowRadius`. */
	@O accessor view3D_shadowRadius: number|n;
	/** Set15 P8 (§6.2) — the directional shadow depth-bias.  NEGATIVE (three's compare is
	 *  `coord.z += bias` then `step(coord.z, depth)` → positive = more self-shadowing).  Default
	 *  -0.0015.  Validated via `resolveShadowBias`. */
	@O accessor view3D_shadowBias: number|n;

	// NOTE: the 3D solve-domain knobs (`mode3D_workingGridRadius`, `mode3D_fineCellSize`,
	// `ringStackEnabled_3d` / `ringStackEnabled_2d`) moved to `PoissonSolverConfig` 2026-08-17 so all solve knobs live in
	// one place on the terrain definition.  `MapSettings_View3D` keeps only activation state
	// (`view3D_enabled`, `view3D_activeViewpointObjID`) + camera/render (`maxViewDistance`,
	// `fogDensity`) + scenery (`view3D_sceneryMaxDistance`) settings.
}

export class SizeReferenceEntry {
	constructor(data?: Partial<SizeReferenceEntry>) { Object.assign(this, data); }
	enabled = true;
	textureId = "";
}